import DateInput from './DateInput';
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Modal, ActionBtn } from './Shared';
import { DOCUMENT_TYPES, getDocumentTypeConfig } from '../constants/documentTypes';
import { doctorProfileAPI } from '../services/api';

/**
 * DocumentUploadModal
 *
 * Universal upload modal for all 7 doctor document types.
 * Lets the admin select a file (drag-drop or click), preview it (image preview
 * or PDF info pill), optionally set an expiry date and notes, and upload via
 * the multipart endpoint.
 *
 * Props:
 *   open               — boolean, controls visibility
 *   onClose            — () => void, called on cancel or successful upload
 *   onSuccess          — (newDocumentRow) => void, called after successful upload
 *   doctorId           — string (UUID), the doctor receiving the document
 *   forcedDocumentType — optional string. If provided, the type is locked
 *                        (used when upload button is per-row in Έγγραφα tab).
 *                        If omitted, admin picks from a dropdown.
 */
export default function DocumentUploadModal({
  open,
  onClose,
  onSuccess,
  doctorId,
  forcedDocumentType = null,
}) {
  const [documentType, setDocumentType] = useState(forcedDocumentType || '');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [rotation, setRotation] = useState(0);
  const fileInputRef = useRef(null);

  // Reset state when the modal opens or the forced type changes
  useEffect(() => {
    if (open) {
      setDocumentType(forcedDocumentType || '');
      setFile(null);
      setPreviewUrl(null);
      setExpirationDate('');
      setNotes('');
      setError(null);
      setUploading(false);
      setDragActive(false);
      setRotation(0);
    }
  }, [open, forcedDocumentType]);

  // Free the preview blob URL when it changes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const config = documentType ? getDocumentTypeConfig(documentType) : null;
  const acceptAttr = config ? config.acceptedMimeTypes.join(',') : '';
  const maxBytesLabel = config ? Math.round(config.maxBytes / 1024 / 1024) + ' MB' : '';

  const handleFilePicked = (picked) => {
    setError(null);

    if (!documentType) {
      setError('Επιλέξτε πρώτα τύπο εγγράφου');
      return;
    }
    if (!picked) return;

    // Client-side mime check (defense in depth — backend re-validates)
    if (!config.acceptedMimeTypes.includes(picked.type)) {
      setError('Μη έγκυρος τύπος αρχείου. Επιτρέπονται: ' + config.acceptedMimeTypes.join(', '));
      return;
    }
    if (picked.size > config.maxBytes) {
      setError(`Το αρχείο είναι πολύ μεγάλο. Μέγιστο: ${maxBytesLabel}`);
      return;
    }

    // Replace previous preview if any
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    if (picked.type.startsWith('image/')) {
      setRotation(0);
      setPreviewUrl(URL.createObjectURL(picked));
    } else {
      setPreviewUrl(null);
    }
  };

  const handleInputChange = (e) => {
    const picked = e.target.files && e.target.files[0];
    handleFilePicked(picked);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const picked = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFilePicked(picked);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!documentType) {
      setError('Επιλέξτε τύπο εγγράφου');
      return;
    }
    if (!file) {
      setError('Επιλέξτε αρχείο');
      return;
    }

    setUploading(true);
    try {
      let fileToUpload = file;
      const isImage = file.type && file.type.startsWith('image/');
      if (isImage && rotation !== 0) {
        try {
          fileToUpload = await rotateImageBlob(file, rotation);
        } catch (rotErr) {
          setError('Αποτυχία περιστροφής εικόνας');
          setUploading(false);
          return;
        }
      }
      const fd = new FormData();
      fd.append('file', fileToUpload);
      fd.append('documentType', documentType);
      if (expirationDate) fd.append('expirationDate', expirationDate);
      if (notes) fd.append('notes', notes);

      const res = await doctorProfileAPI.addDocument(doctorId, fd);
      const data = res.data && res.data.data ? res.data.data : null;
      toast.success('✅ Έγγραφο μεταφορτώθηκε');
      if (onSuccess) onSuccess(data);
      if (onClose) onClose();
    } catch (err) {
      const serverMsg =
        err && err.response && err.response.data && err.response.data.error;
      setError(serverMsg || 'Αποτυχία μεταφόρτωσης');
      setUploading(false);
    }
  };

  // Render an image File to a rotated JPEG Blob via canvas.
  // degrees should be 90 / 180 / 270.
  const rotateImageBlob = (sourceFile, degrees) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(sourceFile);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const swap = degrees === 90 || degrees === 270;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('toBlob returned null')); return; }
          const renamed = new File([blob], sourceFile.name, { type: 'image/jpeg' });
          resolve(renamed);
        },
        'image/jpeg',
        0.95
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });

  const dropZoneStyle = {
    border: dragActive ? '2px dashed var(--primary)' : '2px dashed var(--border)',
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    cursor: documentType ? 'pointer' : 'not-allowed',
    background: dragActive ? 'rgba(192, 24, 90, 0.05)' : '#fafafa',
    transition: 'background 120ms, border-color 120ms',
    opacity: documentType ? 1 : 0.5,
  };

  const fieldLabel = { display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 };
  const inputStyle = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' };

  return (
    <Modal
      open={open}
      onClose={uploading ? () => { /* deliberate no-op: the modal must not be closable while an upload is in flight */ } : onClose}
      title={config ? 'Μεταφόρτωση: ' + config.label : 'Μεταφόρτωση Εγγράφου'}
      size="md"
    >
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!forcedDocumentType && (
          <div>
            <label style={fieldLabel}>Τύπος Εγγράφου</label>
            <select
              value={documentType}
              onChange={(e) => {
                setDocumentType(e.target.value);
                setFile(null);
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
                setError(null);
              }}
              style={inputStyle}
              disabled={uploading}
            >
              <option value="">Επιλέξτε...</option>
              {DOCUMENT_TYPES.map((dt) => (
                <option key={dt.key} value={dt.key}>{dt.label}</option>
              ))}
            </select>
          </div>
        )}

        <div
          style={dropZoneStyle}
          onClick={() => documentType && !uploading && fileInputRef.current && fileInputRef.current.click()}
          onDragOver={documentType && !uploading ? handleDragOver : undefined}
          onDragLeave={handleDragLeave}
          onDrop={documentType && !uploading ? handleDrop : undefined}
        >
          {!file ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Σύρετε αρχείο εδώ ή κάντε κλικ για επιλογή
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {config
                  ? config.acceptedMimeTypes.map((m) => m.replace('application/', '').replace('image/', '')).join(', ').toUpperCase() + ' • Μέγιστο ' + maxBytesLabel
                  : 'Επιλέξτε πρώτα τύπο εγγράφου'}
              </div>
            </div>
          ) : previewUrl ? (
            <div>
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: 6,
                  marginBottom: 8,
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 200ms',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setRotation((r) => (r + 270) % 360); }}
                  style={{ padding: '6px 12px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'white', cursor: 'pointer' }}
                  title="Περιστροφή αριστερά"
                >
                  ↺ Αριστερά
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setRotation((r) => (r + 90) % 360); }}
                  style={{ padding: '6px 12px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'white', cursor: 'pointer' }}
                  title="Περιστροφή δεξιά"
                >
                  ↻ Δεξιά
                </button>
              </div>
              <div style={{ fontSize: 13 }}>
                {file.name} • {Math.round(file.size / 1024)} KB
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontWeight: 600 }}>{file.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {Math.round(file.size / 1024)} KB
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            onChange={handleInputChange}
            style={{ display: 'none' }}
            disabled={!documentType || uploading}
          />
        </div>

        {config && config.hasExpiry && (
          <div>
            <label style={fieldLabel}>Ημερομηνία Λήξης</label>
            <DateInput
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              style={inputStyle}
              disabled={uploading}
            />
          </div>
        )}

        <div>
          <label style={fieldLabel}>Σημειώσεις (προαιρετικό)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
            disabled={uploading}
            placeholder="Σχόλια ή σημειώσεις σχετικά με αυτό το έγγραφο"
          />
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right' }}>
            {notes.length} / 1000
          </div>
        </div>

        {error && (
          <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', borderRadius: 6, padding: 10, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <ActionBtn onClick={onClose} disabled={uploading}>Ακύρωση</ActionBtn>
          <ActionBtn variant="primary" onClick={handleSubmit} disabled={!file || !documentType || uploading}>
            {uploading ? 'Μεταφόρτωση...' : 'Μεταφόρτωση'}
          </ActionBtn>
        </div>
      </div>
    </Modal>
  );
}
